import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, Loader2, MailCheck, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { AuthLayout } from "@/pages/Login";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { getLang } from "@/lib/i18n";

const ROLES = [["employee", "Dipendente", User], ["admin", "Amministratore", ShieldCheck]];

export default function Registrati() {
  const [params] = useSearchParams();
  const invite = params.get("invito");
  const nav = useNavigate();
  const { signIn, loadSettings } = useAuth();
  const brandCode = params.get("codice");
  useEffect(() => { if (brandCode) loadSettings(brandCode); }, [brandCode, loadSettings]);
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ name: "", email: params.get("email") || "", password: "", confirm: "", role: "employee", company_name: "", company_code: params.get("codice") || "" });
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const run = async (fn) => {
    setErr("");
    setBusy(true);
    try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const send = () => api.post("/auth/register", { name: f.name, email: f.email, password: f.password, role: invite ? "employee" : f.role, invite, company_name: f.company_name, company_code: f.company_code, language: getLang() });
  const submit = (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setErr("Le password non coincidono");
    run(async () => { await send(); setStep(2); setCooldown(60); setCode(""); });
  };
  const verify = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/auth/register/verify", { email: f.email, code });
      if (data.status === "approved") {
        signIn(data.user);
        toast.success("Profilo creato, benvenuto!");
        nav("/", { replace: true });
      } else setStep(3);
    });
  };

  const subtitle = { 1: invite ? "Sei stato invitato: crea il tuo profilo da dipendente." : "Crea il tuo account: dopo la verifica dell'email potrai accedere subito.", 2: `Abbiamo inviato un codice di 6 cifre a ${f.email}.`, 3: "" }[step];

  return (
    <AuthLayout title={step === 3 ? "Registrazione inviata" : "Registrati"} subtitle={subtitle}>
      {step === 1 && (
        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          <div className="space-y-1.5"><Label>Nome e cognome</Label><Input value={f.name} onChange={set("name")} required minLength={2} autoComplete="name" data-testid="register-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} required readOnly={!!invite} autoComplete="email" data-testid="register-email-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Password (min. 8)</Label><Input type="password" minLength={8} value={f.password} onChange={set("password")} required autoComplete="new-password" data-testid="register-password-input" /></div>
            <div className="space-y-1.5"><Label>Conferma</Label><Input type="password" minLength={8} value={f.confirm} onChange={set("confirm")} required autoComplete="new-password" data-testid="register-password-confirm-input" /></div>
          </div>
          {!invite && (
            <div className="space-y-1.5">
              <Label>Ruolo</Label>
              <RadioGroup value={f.role} onValueChange={(v) => setF({ ...f, role: v })} className="grid grid-cols-2 gap-2">
                {ROLES.map(([v, l, I]) => (
                  <label key={v} className="flex cursor-pointer items-center gap-2.5 rounded-md border p-3 text-sm transition-colors hover:bg-muted">
                    <RadioGroupItem value={v} data-testid={`register-role-${v}`} /><I className="h-4 w-4 text-primary" />{l}
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}
          {!invite && f.role === "admin" && (
            <div className="space-y-1.5">
              <Label>Nome della tua azienda</Label>
              <Input value={f.company_name} onChange={set("company_name")} required minLength={2} data-testid="register-company-name-input" />
              <p className="text-xs text-muted-foreground">Creerai una nuova azienda e riceverai un codice unico da dare a dipendenti e soci.</p>
            </div>
          )}
          {!invite && f.role === "employee" && (
            <div className="space-y-1.5">
              <Label>Codice azienda</Label>
              <Input value={f.company_code} onChange={(e) => setF({ ...f, company_code: e.target.value.toUpperCase() })} required placeholder="NX-XXXXXX" className="font-mono uppercase" data-testid="register-company-code-input" />
              <p className="text-xs text-muted-foreground">Chiedi il codice al tuo amministratore.</p>
            </div>
          )}
          {err && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-error">{err}</p>}
          <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Continua
          </Button>
        </form>
      )}
      {step === 2 && (
        <form onSubmit={verify} className="space-y-5" data-testid="register-verify-form">
          <div className="flex items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary"><MailCheck className="h-4 w-4" />Controlla la tua casella email (anche lo spam).</div>
          <div className="space-y-2">
            <Label>Codice di verifica</Label>
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="register-code-input">
              <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
            </InputOTP>
          </div>
          {err && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="register-verify-error">{err}</p>}
          <Button type="submit" className="w-full" disabled={busy || code.length !== 6} data-testid="register-verify-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verifica email
          </Button>
          <button type="button" disabled={busy || cooldown > 0} onClick={() => run(async () => { await send(); setCode(""); setCooldown(60); toast.success(`Nuovo codice inviato a ${f.email}`); })} className="text-xs text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60" data-testid="register-resend-button">
            {cooldown > 0 ? `Puoi richiedere un nuovo codice tra ${cooldown} s` : "Non hai ricevuto il codice? Invialo di nuovo"}
          </button>
        </form>
      )}
      {step === 3 && (
        <div className="space-y-4" data-testid="register-pending">
          <div className="flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            <Clock className="mt-0.5 h-5 w-5 shrink-0" />
            <span>Email verificata. Il tuo account è <b>in attesa di approvazione</b> da parte di un amministratore. Riceverai un'email appena sarà approvato.</span>
          </div>
        </div>
      )}
      <p className="mt-6 text-sm text-muted-foreground">
        Hai già un account? <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">Accedi</Link>
      </p>
      {step === 2 && (
        <button onClick={() => setStep(1)} className="mt-2 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground" data-testid="register-back-button"><ArrowLeft className="h-3.5 w-3.5" />Modifica i dati</button>
      )}
    </AuthLayout>
  );
}
